const backendUrl = () => process.env.REACT_APP_BACKEND_URL;

const authHeader = (token) => {
  if (token) return { Authorization: `Bearer ${token}` };
  try {
    const userData = JSON.parse(localStorage.getItem("clientData"));
    if (userData?.token) return { Authorization: `Bearer ${userData.token}` };
  } catch {
    /* ignore */
  }
  return {};
};

const readJson = async (response) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.message || `Request failed (${response.status})`);
    error.code = data.code || response.status;
    error.payload = data;
    throw error;
  }
  return data;
};

export const ANALYZE_HARD_TIMEOUT_MS = 25000;

export const analyzeSpreadsheet = async (file, { timeoutMs = ANALYZE_HARD_TIMEOUT_MS, signal } = {}) => {
  const form = new FormData();
  form.append("file", file);
  const controller = new AbortController();
  const onOuterAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener("abort", onOuterAbort, { once: true });
  }
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(`${backendUrl()}/start-import/analyze`, {
      method: "POST",
      body: form,
      headers: authHeader(),
      signal: controller.signal,
    });
    return readJson(response);
  } catch (err) {
    if (err?.name === "AbortError") {
      const error = new Error("analyze_timeout");
      error.code = "ANALYZE_TIMEOUT";
      throw error;
    }
    throw err;
  } finally {
    clearTimeout(timer);
    if (signal) signal.removeEventListener("abort", onOuterAbort);
  }
};

export const matchPassengers = async (passengers, { signal } = {}) => {
  const response = await fetch(`${backendUrl()}/start-import/match`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body: JSON.stringify({
      passengers: (passengers || [])
        .filter((passenger) => passenger?.name)
        .map((passenger) => ({
          rowNumber: passenger.rowNumber,
          name: passenger.name,
          schoolName: passenger.schoolName || "",
          identifier: passenger.identifier || "",
          phone: passenger.phone || "",
          parents: (passenger.parents || []).map((parent) => ({ phone: parent.phone || "" })),
        })),
    }),
    signal,
  });
  return readJson(response);
};

export const previewImportedRoutes = async (payload) => {
  const response = await fetch(`${backendUrl()}/start-import/preview-routes`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body: JSON.stringify(payload),
  });
  return readJson(response);
};

// The commit runs as a server-side job: POST answers at once with a jobId, GET /jobs/:id is
// polled for real progress (phase, rows written, names) until it is done or failed.
// `token` lets the caller act with a session that is not yet the app's active login
// (a workspace created a moment ago, before auth.login remounts the tree).
export const startCommitJob = async (payload, { token } = {}) => {
  const response = await fetch(`${backendUrl()}/start-import/commit`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeader(token) },
    body: JSON.stringify(payload),
  });
  return readJson(response);
};

export const getCommitJob = async (jobId, { token, signal } = {}) => {
  const response = await fetch(`${backendUrl()}/start-import/jobs/${encodeURIComponent(jobId)}`, {
    method: "GET",
    headers: { ...authHeader(token) },
    signal,
  });
  return readJson(response);
};

export const JOB_POLL_INTERVAL_MS = 1000;
// A job whose state has not changed for this long is treated as lost (the server would have
// failed it itself well before; this only guards against a dead connection that never errors).
export const JOB_STALL_MS = 5 * 60 * 1000;
// Transient network failures in a row before giving up on watching (the job itself continues).
export const JOB_MAX_NETWORK_FAILURES = 20;

const sleep = (ms, signal) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    if (signal) {
      signal.addEventListener(
        "abort",
        () => {
          clearTimeout(timer);
          reject(Object.assign(new Error("aborted"), { name: "AbortError" }));
        },
        { once: true },
      );
    }
  });

const isSessionError = (err) => err?.code === 401 || err?.code === 403;

// Watches a job until it settles. Resolves with the job result (plus jobId); rejects with an
// Error carrying `code` (job error code, JOB_LOST, JOB_STALLED, or an HTTP status) and `payload`.
export const waitForCommitJob = async (jobId, { token, onProgress, signal, intervalMs = JOB_POLL_INTERVAL_MS } = {}) => {
  let networkFailures = 0;
  let lastChange = Date.now();
  let lastUpdatedAt = null;
  for (;;) {
    if (signal?.aborted) throw Object.assign(new Error("aborted"), { name: "AbortError" });
    let job;
    try {
      job = await getCommitJob(jobId, { token, signal });
      networkFailures = 0;
    } catch (err) {
      if (err?.name === "AbortError") throw err;
      if (err?.code === 404 || err?.code === "JOB_NOT_FOUND") {
        // The server forgot the job (restart / TTL). Whatever it wrote is in the workspace.
        throw Object.assign(new Error("This import is no longer being tracked. Check your students before saving again."), {
          code: "JOB_LOST",
          payload: err?.payload || null,
        });
      }
      if (isSessionError(err)) throw err;
      networkFailures += 1;
      if (networkFailures >= JOB_MAX_NETWORK_FAILURES) {
        throw Object.assign(new Error("Lost the connection while saving. The import continues on the server — reload to see where it got to."), {
          code: "JOB_UNREACHABLE",
        });
      }
      await sleep(intervalMs, signal);
      continue;
    }

    if (typeof onProgress === "function") {
      try {
        onProgress(job);
      } catch {
        /* a UI listener must not stop the watch */
      }
    }
    if (job.phase === "done") return { ...(job.result || {}), jobId };
    if (job.phase === "failed") {
      const error = job.error || {};
      throw Object.assign(new Error(error.message || "Import failed."), {
        code: error.code || error.status || "JOB_FAILED",
        payload: error,
      });
    }
    if (job.updatedAt !== lastUpdatedAt) {
      lastUpdatedAt = job.updatedAt;
      lastChange = Date.now();
    } else if (Date.now() - lastChange > JOB_STALL_MS) {
      throw Object.assign(new Error("Saving stopped making progress. Reload to see what was written."), { code: "JOB_STALLED" });
    }
    await sleep(intervalMs, signal);
  }
};

// Start + watch in one call; kept as the single entry point the journey uses.
export const commitStartImport = async (payload, { token, onProgress, signal, intervalMs } = {}) => {
  const started = await startCommitJob(payload, { token });
  if (typeof onProgress === "function") onProgress({ phase: "queued", done: 0, total: started.total || 0, counts: {}, recent: [], jobId: started.jobId });
  return waitForCommitJob(started.jobId, { token, onProgress, signal, intervalMs });
};
